import React from 'react'
import PropTypes from 'prop-types'
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop'

/**
 * The crop tool of the upload tab.
 *
 * # It rendered NOTHING until now
 *
 * The whole `<ReactCrop>` and its stylesheet were commented out and the component returned an
 * empty Fragment. That broke the tab end to end: `onLoad` is what set `image.croppedImage`, and
 * the Submit button is disabled until that exists — so a user could pick a file, see an empty
 * 300px box, and never submit.
 *
 * The reason it was commented is visible in the code that was left behind: it uses
 * `react-image-crop`'s **v8** API —`src`, `onImageLoaded`, `imageStyle`— and the installed
 * version is **11**, where none of those props exist. The 174-line copy of the library's
 * stylesheet next to it was stale for the same reason: v11 renders classes v8 never had
 * (`__child-wrapper`, `__crop-mask`, `__drag-handle`).
 *
 * This is the v11 shape: the `<img>` is a CHILD, the aspect is its own prop, and the stylesheet
 * comes from the library through `entry.css` instead of a copy that can go stale again.
 */
export const ImageCrop = ({ image, setImage, crop, setCrop, circularCrop, aspect = 1 }) => {
  const imgRef = React.useRef(null)

  /**
   * Seeds a centred crop of the right shape once the image knows its size.
   *
   * `makeAspectCrop` needs the rendered dimensions, which is why this cannot happen before load.
   * The seed also writes `croppedImage` so Submit is reachable without dragging anything — the
   * whole image IS a valid crop.
   */
  const onImageLoad = event => {
    const { width, height } = event.currentTarget
    const next = centerCrop(makeAspectCrop({ unit: '%', width: 100 }, aspect, width, height), width, height)
    setCrop(next)
    draw(convertToPixelCrop(next, width, height))
  }

  /** Paints the selected region onto a canvas and stores it as a data url. */
  const draw = pixelCrop => {
    const img = imgRef.current
    if (!img || !pixelCrop?.width || !pixelCrop?.height) return

    // The element is scaled down to fit the box, so the crop has to be scaled back up to the
    // image's real pixels or the result comes out cropped from the wrong place.
    const scaleX = img.naturalWidth / img.width
    const scaleY = img.naturalHeight / img.height

    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(pixelCrop.width * scaleX)
    canvas.height = Math.ceil(pixelCrop.height * scaleY)

    canvas.getContext('2d').drawImage(
      img,
      pixelCrop.x * scaleX, pixelCrop.y * scaleY,
      pixelCrop.width * scaleX, pixelCrop.height * scaleY,
      0, 0,
      pixelCrop.width * scaleX, pixelCrop.height * scaleY
    )

    setImage(current => ({ ...current, croppedImage: canvas.toDataURL('image/png') }))
  }

  return (
    <ReactCrop
      crop={crop}
      aspect={aspect}
      circularCrop={circularCrop}
      onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
      onComplete={pixelCrop => draw(pixelCrop)}
    >
      {/* v11 takes the image as a child instead of a `src` prop. */}
      <img
        ref={imgRef}
        src={image?.data}
        alt='Crop preview'
        onLoad={onImageLoad}
        style={{ height: '300px', maxWidth: '100%' }}
      />
    </ReactCrop>
  )
}

ImageCrop.propTypes = {
  /** `{ data, file, croppedImage }` — `data` is the base64 the file reader produced. */
  image: PropTypes.object,
  setImage: PropTypes.func.isRequired,
  crop: PropTypes.object,
  setCrop: PropTypes.func.isRequired,
  circularCrop: PropTypes.bool,
  /** `1` for a square, `16 / 9` for landscape. */
  aspect: PropTypes.number
}
